import dashboardTemplatesFile from './dashboardTemplates.json';

/** Default Material icon for a dashboard. */
export const DEFAULT_ICON = 'mdi-view-dashboard';

/** Header graphic: Material icon (default) or the default site's photo. */
export const HEADER_ART_OPTIONS = [
	{ text: 'Icon', value: 'icon' },
	{ text: 'Site photo', value: 'sitePhoto' }
];

/** Desktop layout columns. Stored card `layout.w` / `layout.x` use this. */
export const GRID_COLUMNS = 12;

/** CSS grid track height. Two small cards (h: 2) plus one gap equal one regular card (h: 4). */
export const GRID_ROW = 81;

/** CSS grid gap. Same on both axes. */
export const GRID_GAP = 16;

/** Smallest card height in grid rows. Dividers may be 1. */
export const GRID_MIN_H = 2;

/** Max timestamped rows kept on a text card in log mode. */
export const TEXT_LOG_MAX = 100;

/**
 * Smallest height in grid rows for a card type.
 * @param {string} [type]
 * @returns {number}
 */
export function minLayoutH(type) {
	return type === 'divider' ? 1 : GRID_MIN_H;
}

/** Preview / breakpoint column counts. */
export const VIEWPORTS = [
	{ id: 'auto', label: 'Live', icon: 'mdi-monitor-screenshot', cols: null, width: null },
	{ id: 'desktop', label: 'Desktop', icon: 'mdi-monitor', cols: 12, width: 1280 },
	{ id: 'tablet', label: 'Tablet', icon: 'mdi-tablet', cols: 6, width: 768 },
	{ id: 'mobile', label: 'Mobile', icon: 'mdi-cellphone', cols: 1, width: 390 }
];

/**
 * Column count for a canvas width in pixels.
 * @param {number} px
 * @returns {number}
 */
export function colsForWidth(px) {
	if (px <= 600) return 1;
	if (px <= 1100) return 6;
	return GRID_COLUMNS;
}

/**
 * Pixel size of one snapped cell in a CSS grid.
 * @param {number} gridWidth
 * @param {number} cols
 * @returns {number}
 */
export function columnWidthPx(gridWidth, cols) {
	const n = cols || GRID_COLUMNS;
	return (gridWidth - GRID_GAP * Math.max(0, n - 1)) / n;
}

/**
 * Snap a layout to integer 12-col cells.
 * @param {object} layout
 * @param {string} [type]
 * @returns {{ x: number, y: number, w: number, h: number }}
 */
export function snapLayout(layout, type) {
	return clampLayout({
		x: Math.round(Number(layout && layout.x) || 0),
		y: Math.round(Number(layout && layout.y) || 0),
		w: Math.round(Number(layout && layout.w) || 1),
		h: Math.round(Number(layout && layout.h) || minLayoutH(type))
	}, type);
}

/**
 * Reflow 12-col cards into `cols` columns in reading order (wrap).
 * Desktop (12) keeps authored gaps; tablet/mobile pack and wrap.
 * @param {Array} cards
 * @param {number} cols
 * @returns {Object<string, { x: number, y: number, w: number, h: number }>}
 */
export function packCardsForCols(cards, cols) {
	const n = cols || GRID_COLUMNS;
	const list = cards || [];
	if (n >= GRID_COLUMNS) {
		const out = {};
		list.forEach(card => {
			out[card.uniqueId] = clampLayout(card.layout, card.type);
		});
		return out;
	}
	const ordered = list.slice().sort((a, b) => {
		const la = clampLayout(a.layout, a.type);
		const lb = clampLayout(b.layout, b.type);
		if (la.y !== lb.y) return la.y - lb.y;
		return la.x - lb.x;
	});
	const out = {};
	let x = 0;
	let y = 0;
	let rowH = 1;
	ordered.forEach(card => {
		const src = clampLayout(card.layout, card.type);
		const w = n === 1 ? 1 : Math.min(src.w, n);
		const h = src.h;
		if (x > 0 && x + w > n) {
			x = 0;
			y += rowH;
			rowH = h;
		} else {
			rowH = Math.max(rowH, h);
		}
		out[card.uniqueId] = { x, y, w, h };
		x += w;
		if (x >= n) {
			x = 0;
			y += rowH;
			rowH = 1;
		}
	});
	return out;
}

/** Palette + default layout for each v2 card type. */
export const CARD_TYPES = [
	{ type: 'kpi', label: 'KPI', icon: 'mdi-speedometer', w: 3, h: 2 },
	{ type: 'trend', label: 'Trend Chart', icon: 'mdi-chart-line', w: 6, h: 4 },
	{ type: 'donut', label: 'Donut Chart', icon: 'mdi-chart-donut', w: 6, h: 4 },
	{ type: 'bar', label: 'Bar Chart', icon: 'mdi-chart-bar', w: 6, h: 4 },
	{ type: 'equipment', label: 'Status', icon: 'mdi-cube-outline', w: 12, h: 2 },
	{ type: 'alerts', label: 'Alert Feed', icon: 'mdi-bell-outline', w: 4, h: 4 },
	{ type: 'energy', label: 'KPI Bar', icon: 'mdi-flash-outline', w: 6, h: 2 },
	{ type: 'zoneMap', label: 'Zone Map', icon: 'mdi-map-outline', w: 8, h: 5, disabled: true },
	{ type: 'workOrders', label: 'Work Orders', icon: 'mdi-clipboard-text-outline', w: 6, h: 4, disabled: true },
	{ type: 'text', label: 'Text / Note', icon: 'mdi-format-text', w: 6, h: 2 },
	{ type: 'divider', label: 'Divider', icon: 'mdi-minus', w: 12, h: 1 }
];

export const TIME_RANGES = [
	{ name: 'Today', value: 'day' },
	{ name: 'This Week', value: 'week' },
	{ name: 'This Month', value: 'month' },
	{ name: 'Custom', value: 'custom' }
];

export const DASHBOARD_ICONS = [
	'mdi-hvac',
	'mdi-air-conditioner',
	'mdi-air-filter',
	'mdi-fan',
	'mdi-snowflake',
	'mdi-thermometer',
	'mdi-home-thermometer-outline',
	'mdi-coolant-temperature',
	'mdi-pipe',
	'mdi-water-percent',
	'mdi-water-outline',
	'mdi-water',
	'mdi-weather-windy',
	'mdi-molecule-co2',
	'mdi-blur',
	'mdi-blur-radial',
	'mdi-flask-outline',
	'mdi-atom',
	'mdi-virus-outline',
	'mdi-weather-sunny',
	'mdi-leaf',
	'mdi-auto-fix',
	'mdi-flash',
	'mdi-flash-outline',
	'mdi-lightbulb-outline',
	'mdi-shield-outline',
	'mdi-lock-outline',
	'mdi-clock-outline',
	'mdi-clock-check-outline',
	'mdi-timer-outline',
	'mdi-view-dashboard',
	'mdi-office-building',
	'mdi-heart-pulse',
	'mdi-chart-areaspline',
	'mdi-chart-line',
	'mdi-chart-bar',
	'mdi-chart-donut',
	'mdi-home-floor-1',
	'mdi-wrench',
	'mdi-account-group',
	'mdi-target',
	'mdi-bell-ring',
	'mdi-bell-outline',
	'mdi-speedometer'
];

/** Menu props for dashboard/card icon autocomplete (full list, not clipped). */
export const DASHBOARD_ICON_MENU_PROPS = {
	bottom: true,
	offsetY: true,
	closeOnClick: true,
	closeOnContentClick: true,
	contentClass: 'db2-icon-menu',
	maxHeight: 400,
	zIndex: 300
};

/**
 * Keep every icon visible when Vuetify seeds search with the selected label.
 * @param {string|{ value?: string }} item
 * @param {string} queryText
 * @param {string} itemText
 * @param {string} selectedLabel
 * @returns {boolean}
 */
export function filterDashboardIcon(item, queryText, itemText, selectedLabel) {
	const q = String(queryText || '').trim().toLowerCase();
	if (!q) return true;
	const selected = String(selectedLabel || '').trim().toLowerCase();
	if (selected && q === selected) return true;
	const text = String(itemText || '').toLowerCase();
	const value = String((item && typeof item === 'object' ? item.value : item) || '').toLowerCase();
	return text.indexOf(q) > -1 || value.indexOf(q) > -1;
}

/**
 * Create a unique id with a prefix.
 * @param {string} prefix
 * @returns {string}
 */
export function makeId(prefix) {
	return prefix + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

/**
 * One timestamped row for a text card in log mode.
 * @param {string} text
 * @param {{ objectId?: string, id?: string, firstName?: string, lastName?: string, username?: string, email?: string }} [user]
 * @returns {{ id: string, at: string, text: string, userId: string, userName: string }}
 */
export function newTextLogEntry(text, user) {
	const u = user || {};
	const userName = [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.username || u.email || 'User';
	return {
		id: makeId('note'),
		at: new Date().toISOString(),
		text: String(text || '').trim(),
		userId: u.objectId || u.id || '',
		userName
	};
}

/**
 * Append a log row and drop the oldest past TEXT_LOG_MAX.
 * @param {object} config
 * @param {{ id: string, at: string, text: string, userId: string, userName: string }} entry
 * @returns {object}
 */
export function appendTextLog(config, entry) {
	const cfg = { ...(config || {}), mode: 'log' };
	const prev = Array.isArray(cfg.entries) ? cfg.entries : [];
	cfg.entries = prev.concat(entry).slice(-TEXT_LOG_MAX);
	return cfg;
}

/**
 * True when this dashboard uses the v2 schema.
 * @param {object|null} dashboard
 * @returns {boolean}
 */
export function isV2Dashboard(dashboard) {
	return !!(dashboard && Number(dashboard.schemaVersion) === 2);
}

/**
 * Site objectId from a dashboard pointer or string.
 * @param {object|null} dashboard
 * @returns {string|null}
 */
export function dashboardSiteId(dashboard) {
	if (!dashboard || !dashboard.site) return null;
	const site = dashboard.site;
	if (typeof site === 'string') return site;
	return site.objectId || site.id || null;
}

/**
 * Header graphic mode. Defaults to icon.
 * @param {object|null} dashboard
 * @returns {'icon'|'sitePhoto'}
 */
export function dashboardHeaderArt(dashboard) {
	return dashboard && dashboard.headerArt === 'sitePhoto' ? 'sitePhoto' : 'icon';
}

/**
 * HTTPS URL for a site logo, or empty when none is set.
 * @param {object|null} site
 * @returns {string}
 */
export function siteLogoUrl(site) {
	const url = site && site.logo && site.logo.url;
	if (!url) return '';
	return String(url).replace(/^http:\/\//i, 'https://');
}

/**
 * Site photo URL when the dashboard header uses the site photo.
 * @param {object|null} dashboard
 * @param {Array} sites
 * @returns {string}
 */
export function dashboardHeaderPhotoUrl(dashboard, sites) {
	if (dashboardHeaderArt(dashboard) !== 'sitePhoto') return '';
	const id = dashboardSiteId(dashboard) || (dashboard && dashboard.siteId) || null;
	if (!id || !Array.isArray(sites)) return '';
	const site = sites.find(function(row) {
		return row && row.objectId === id;
	});
	return siteLogoUrl(site);
}

/**
 * Route query for opening a dashboard. No default site omits siteId so breadcrumbs show All Sites.
 * @param {object|null} dashboard
 * @param {object} [base]
 * @returns {object}
 */
export function dashboardSiteQuery(dashboard, base) {
	const query = Object.assign({}, base || {});
	const siteId = dashboardSiteId(dashboard);
	if (siteId) query.siteId = siteId;
	else delete query.siteId;
	return query;
}

/**
 * Card type meta or KPI fallback.
 * @param {string} type
 * @returns {object}
 */
export function cardTypeMeta(type) {
	return CARD_TYPES.find(t => t.type === type) || CARD_TYPES[0];
}

/**
 * Default config for a new card type.
 * @param {string} type
 * @returns {object}
 */
export function defaultCardConfig(type) {
	switch (type) {
		case 'kpi':
			return { variant: 'sparkline', icon: 'mdi-thermometer', unit: '', showAxisLabels: false };
		case 'trend':
			return {
				series: [
					{ label: 'Temperature', color: '#5b8def' },
					{ label: 'Humidity', color: '#4ade80' },
					{ label: 'CO2', color: '#e45b73' }
				]
			};
		case 'donut':
			return { centerLabel: 'AVG SCORE' };
		case 'bar':
			return { orientation: 'horizontal', variant: 'absolute' };
		case 'equipment':
			return { variant: 'icons' };
		case 'alerts':
			return { limit: 8 };
		case 'zoneMap':
			return { title: 'Zone Map', view: 'map' };
		case 'energy':
			return { variant: 'bars' };
		case 'workOrders':
			return {};
		case 'text':
			return { mode: 'note', body: '', entries: [] };
		case 'divider':
			return { label: '' };
		default:
			return {};
	}
}

/**
 * Whether two layout rects overlap.
 * @param {object} a
 * @param {object} b
 * @returns {boolean}
 */
export function layoutsOverlap(a, b) {
	return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/**
 * First free 12-col slot in a section.
 * @param {Array} cards
 * @param {string} sectionId
 * @param {number} w
 * @param {number} h
 * @returns {{ x: number, y: number, w: number, h: number }}
 */
export function findFreeSlot(cards, sectionId, w, h) {
	const width = Math.min(GRID_COLUMNS, Math.max(1, w));
	const height = Math.max(1, h);
	const occupied = (cards || []).filter(c => c.sectionId === sectionId).map(c => c.layout || { x: 0, y: 0, w: 3, h: 1 });
	for (let y = 0; y < 48; y++) {
		for (let x = 0; x <= GRID_COLUMNS - width; x++) {
			const next = { x, y, w: width, h: height };
			const hit = occupied.some(o => layoutsOverlap(next, o));
			if (!hit) return next;
		}
	}
	return { x: 0, y: 0, w: width, h: height };
}

/**
 * Slot below every card in a section (left-aligned on a new row).
 * @param {Array} cards
 * @param {string} sectionId
 * @param {number} w
 * @param {number} h
 * @returns {{ x: number, y: number, w: number, h: number }}
 */
export function findBottomSlot(cards, sectionId, w, h) {
	const width = Math.min(GRID_COLUMNS, Math.max(1, w));
	const height = Math.max(1, h);
	const occupied = (cards || []).filter(c => (c.sectionId || '') === (sectionId || '')).map(c => clampLayout(c.layout, c.type));
	let y = 0;
	occupied.forEach(o => {
		y = Math.max(y, o.y + o.h);
	});
	return { x: 0, y, w: width, h: height };
}

/**
 * Clamp a layout into the 12-col grid.
 * @param {object} layout
 * @param {string} [type]
 * @returns {{ x: number, y: number, w: number, h: number }}
 */
export function clampLayout(layout, type) {
	const minH = minLayoutH(type);
	const w = Math.min(GRID_COLUMNS, Math.max(1, Math.round(Number(layout && layout.w) || 1)));
	const h = Math.min(16, Math.max(minH, Math.round(Number(layout && layout.h) || minH)));
	const x = Math.min(GRID_COLUMNS - w, Math.max(0, Math.round(Number(layout && layout.x) || 0)));
	const y = Math.max(0, Math.round(Number(layout && layout.y) || 0));
	return { x, y, w, h };
}

/**
 * Give every card a stable uniqueId. Missing ids make settings Save a no-op
 * (replace-by-id never matches) and hydrate would mint a new id each time.
 * @param {Array} cards
 * @returns {Array}
 */
export function ensureCardIds(cards) {
	return (cards || []).map(c => {
		if (c && c.uniqueId) return c;
		return { ...c, uniqueId: makeId('card') };
	});
}

/**
 * True when two snapped layouts occupy the same cells.
 * @param {object} a
 * @param {object} b
 * @returns {boolean}
 */
export function layoutsEqual(a, b) {
	return !!a && !!b && a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

/**
 * Reading order: top-to-bottom, then left-to-right, then uniqueId.
 * @param {object} a
 * @param {object} b
 * @returns {number}
 */
function compareReadingOrder(a, b) {
	const la = clampLayout(a.layout, a.type);
	const lb = clampLayout(b.layout, b.type);
	if (la.y !== lb.y) return la.y - lb.y;
	if (la.x !== lb.x) return la.x - lb.x;
	return String(a.uniqueId || '').localeCompare(String(b.uniqueId || ''));
}

/**
 * First free 12-col cell for w×h, scanning row by row left-to-right.
 * Occupied rects (including a pinned hover cell) are skipped.
 * @param {Array<{ x: number, y: number, w: number, h: number }>} occupied
 * @param {number} w
 * @param {number} h
 * @returns {{ x: number, y: number, w: number, h: number }}
 */
function findFirstFit(occupied, w, h) {
	const width = Math.min(GRID_COLUMNS, Math.max(1, w));
	const height = Math.max(1, h);
	let maxY = 0;
	(occupied || []).forEach(o => {
		maxY = Math.max(maxY, (o.y || 0) + (o.h || 0));
	});
	const yLimit = Math.max(96, maxY + height);
	for (let y = 0; y < yLimit; y++) {
		for (let x = 0; x <= GRID_COLUMNS - width; x++) {
			const next = { x, y, w: width, h: height };
			if (!(occupied || []).some(o => layoutsOverlap(next, o))) return next;
		}
	}
	return { x: 0, y: yLimit, w: width, h: height };
}

/**
 * uniqueIds in reading order, optionally omitting a pinned card.
 * @param {Array} cards
 * @param {string} [excludeId]
 * @returns {string[]}
 */
export function readingOrderIds(cards, excludeId) {
	return (cards || []).slice()
		.filter(c => !excludeId || c.uniqueId !== excludeId)
		.sort(compareReadingOrder)
		.map(c => c.uniqueId);
}

/**
 * Bin-pack one section. `pinnedId` keeps its current cell; everyone else
 * fills remaining cells in reading order (no hole a later card can slide into).
 * @param {Array} group
 * @param {string|null} pinnedId
 * @param {string[]} [orderIds]
 * @returns {Array}
 */
function packSectionGroup(group, pinnedId, orderIds) {
	const list = (group || []).map(c => ({ ...c, layout: clampLayout(c.layout, c.type) }));
	const pinned = pinnedId ? list.find(c => c.uniqueId === pinnedId) : null;
	let others = list.filter(c => !pinned || c.uniqueId !== pinned.uniqueId);
	if (orderIds && orderIds.length) {
		const rank = {};
		orderIds.forEach((id, i) => {
			rank[id] = i;
		});
		others.sort((a, b) => {
			const ra = a.uniqueId in rank ? rank[a.uniqueId] : 1e9;
			const rb = b.uniqueId in rank ? rank[b.uniqueId] : 1e9;
			if (ra !== rb) return ra - rb;
			return compareReadingOrder(a, b);
		});
	} else {
		others = others.sort(compareReadingOrder);
	}
	const occupied = [];
	const placed = [];
	if (pinned) {
		occupied.push(pinned.layout);
		placed.push(pinned);
	}
	others.forEach(card => {
		const layout = findFirstFit(occupied, card.layout.w, card.layout.h);
		occupied.push(layout);
		placed.push({ ...card, layout });
	});
	return placed;
}

/**
 * Dense 12-col pack per section, top-to-bottom then left-to-right.
 * Dividers count as cards.
 * @param {Array} cards
 * @returns {Array}
 */
export function packCards(cards) {
	const list = ensureCardIds(cards).map(c => ({ ...c, layout: clampLayout(c.layout, c.type) }));
	const sectionIds = [];
	const seen = {};
	list.forEach(c => {
		const id = c.sectionId || '';
		if (seen[id]) return;
		seen[id] = true;
		sectionIds.push(id);
	});
	const byId = {};
	sectionIds.forEach(sid => {
		packSectionGroup(list.filter(c => (c.sectionId || '') === sid), null).forEach(c => {
			byId[c.uniqueId] = c;
		});
	});
	return list.map(c => byId[c.uniqueId] || c);
}

/**
 * Pin `movedId` at its current cell and bin-pack the rest of that section
 * into remaining cells (reading order). Other sections are unchanged.
 * @param {Array} cards
 * @param {string} movedId
 * @param {string[]} [orderIds]
 * @returns {Array}
 */
export function resolveOverlaps(cards, movedId, orderIds) {
	const list = ensureCardIds(cards).map(c => ({ ...c, layout: clampLayout(c.layout, c.type) }));
	const moved = list.find(c => c.uniqueId === movedId);
	if (!moved) return packCards(list);
	const sid = moved.sectionId || '';
	const rest = list.filter(c => (c.sectionId || '') !== sid);
	const packed = packSectionGroup(list.filter(c => (c.sectionId || '') === sid), movedId, orderIds);
	const byId = {};
	rest.concat(packed).forEach(c => {
		byId[c.uniqueId] = c;
	});
	return list.map(c => byId[c.uniqueId] || c);
}

/**
 * Slot for a duplicate: right of source, else below, else first free cell.
 * @param {object} src
 * @param {Array} cards
 * @returns {{ x: number, y: number, w: number, h: number }}
 */
export function findDuplicateLayout(src, cards) {
	const l = clampLayout(src && src.layout, src && src.type);
	const sectionCards = (cards || []).filter(c => c.sectionId === src.sectionId);
	const right = { x: l.x + l.w, y: l.y, w: l.w, h: l.h };
	if (right.x + right.w <= GRID_COLUMNS && !sectionCards.some(c => layoutsOverlap(right, clampLayout(c.layout, c.type)))) {
		return right;
	}
	const below = { x: l.x, y: l.y + l.h, w: l.w, h: l.h };
	if (!sectionCards.some(c => layoutsOverlap(below, clampLayout(c.layout, c.type)))) {
		return below;
	}
	return findFreeSlot(cards, src.sectionId, l.w, l.h);
}

/**
 * Drop unused grid rows after KPI cards shrink from 2 rows to 1.
 * @param {Array} cards
 * @returns {Array}
 */
function collapseEmptyRows(cards) {
	const list = cards || [];
	const sectionIds = [];
	const seen = {};
	list.forEach(c => {
		const id = c.sectionId || '';
		if (seen[id]) return;
		seen[id] = true;
		sectionIds.push(id);
	});
	const yMap = {};
	sectionIds.forEach(sid => {
		const group = list.filter(c => (c.sectionId || '') === sid);
		let max = 0;
		group.forEach(c => {
			max = Math.max(max, c.layout.y + c.layout.h);
		});
		const used = [];
		for (let i = 0; i < max; i++) used[i] = false;
		group.forEach(c => {
			for (let i = c.layout.y; i < c.layout.y + c.layout.h; i++) used[i] = true;
		});
		const map = [];
		let nextY = 0;
		for (let i = 0; i < max; i++) {
			map[i] = used[i] ? nextY++ : -1;
		}
		yMap[sid] = map;
	});
	return list.map(c => {
		const map = yMap[c.sectionId || ''];
		return { ...c, layout: { ...c.layout, y: map[c.layout.y] } };
	});
}

/**
 * Half-height cards are 2 rows (charts are 4). Restore 1-row cards and shrink tall equipment/energy.
 * @param {Array} cards
 * @returns {Array}
 */
export function shrinkLegacyKpiLayouts(cards) {
	let next = (cards || []).map(c => ({ ...c, layout: { ...(c.layout || {}) } }));
	const expandYs = [];
	next.forEach(c => {
		const h = Math.round(Number(c.layout && c.layout.h) || GRID_MIN_H);
		const y = Math.round(Number(c.layout && c.layout.y) || 0);
		if (c.type !== 'divider' && h < GRID_MIN_H && expandYs.indexOf(y) === -1) expandYs.push(y);
	});
	expandYs.sort((a, b) => b - a);
	expandYs.forEach(y => {
		next = next.map(c => {
			const ly = Math.round(Number(c.layout && c.layout.y) || 0);
			const lh = Math.round(Number(c.layout && c.layout.h) || GRID_MIN_H);
			if (c.type !== 'divider' && ly === y && lh < GRID_MIN_H) {
				return { ...c, layout: { ...c.layout, y: ly, h: GRID_MIN_H } };
			}
			if (ly > y) {
				return { ...c, layout: { ...c.layout, y: ly + 1 } };
			}
			return c;
		});
	});
	next = next.map(c => ({ ...c, layout: clampLayout(c.layout, c.type) }));
	let shrunk = false;
	next = next.map(c => {
		if ((c.type === 'equipment' || c.type === 'energy') && c.layout.h === 3) {
			shrunk = true;
			return { ...c, layout: { ...c.layout, h: 2 } };
		}
		return c;
	});
	return shrunk ? collapseEmptyRows(next) : next;
}

/**
 * Build a new section record.
 * @param {string} name
 * @param {number} sortOrder
 * @returns {object}
 */
export function newSection(name, sortOrder) {
	return {
		uniqueId: makeId('section'),
		name: name || 'Section',
		sortOrder: sortOrder || 0,
		collapsed: false
	};
}

/**
 * Build a new card in a section.
 * @param {string} type
 * @param {string} sectionId
 * @param {Array} cards
 * @returns {object}
 */
export function newCard(type, sectionId, cards) {
	const meta = cardTypeMeta(type);
	return {
		uniqueId: makeId('card'),
		sectionId,
		type: meta.type,
		name: meta.label,
		layout: findFreeSlot(cards, sectionId, meta.w, meta.h),
		config: defaultCardConfig(meta.type)
	};
}

/**
 * Deep-clone dashboard editor state.
 * @param {object} draft
 * @returns {object}
 */
export function cloneDraft(draft) {
	return JSON.parse(JSON.stringify(draft));
}

/**
 * Payload for POST /api/dashboards/:id
 * @param {object} draft
 * @returns {object}
 */
export function savePayload(draft) {
	return {
		name: draft.name,
		description: draft.description || '',
		icon: draft.icon || DEFAULT_ICON,
		headerArt: dashboardHeaderArt(draft),
		folderId: draft.folderId || '',
		defaultTimeRange: draft.defaultTimeRange || 'week',
		schemaVersion: 2,
		sections: draft.sections || [],
		cards: draft.cards || [],
		site: dashboardSiteId(draft) || draft.siteId || null
	};
}

/**
 * Editor draft from a Parse dashboard JSON.
 * @param {object} dashboard
 * @returns {object}
 */
export function draftFromDashboard(dashboard) {
	const v2 = isV2Dashboard(dashboard);
	return {
		objectId: dashboard.objectId,
		name: dashboard.name || 'Untitled',
		description: dashboard.description || '',
		icon: dashboard.icon || DEFAULT_ICON,
		headerArt: dashboardHeaderArt(dashboard),
		folderId: dashboard.folderId || '',
		defaultTimeRange: dashboard.defaultTimeRange || 'week',
		schemaVersion: v2 ? 2 : dashboard.schemaVersion || 1,
		site: dashboard.site || null,
		siteId: dashboardSiteId(dashboard),
		sections: v2 ? (dashboard.sections || []).slice() : [],
		cards: v2 ? shrinkLegacyKpiLayouts(ensureCardIds(dashboard.cards || [])) : []
	};
}

/**
 * Create-dialog template list (Custom first).
 * @returns {Array}
 */
export function listDashboardTemplates() {
	return (dashboardTemplatesFile && dashboardTemplatesFile.templates) || [];
}

/**
 * Fresh sections/cards from a template so ids do not collide.
 * @param {object|null} template
 * @returns {{ sections: Array, cards: Array, icon: string, description: string, defaultTimeRange: string }}
 */
export function cloneDashboardTemplate(template) {
	const src = template || {};
	const idMap = {};
	const sections = (src.sections || []).map(s => {
		const uniqueId = makeId('section');
		if (s && s.uniqueId) idMap[s.uniqueId] = uniqueId;
		return {
			uniqueId,
			name: s && s.name != null ? s.name : 'Section',
			sortOrder: (s && s.sortOrder) || 0,
			collapsed: !!(s && s.collapsed)
		};
	});
	const cards = (src.cards || []).map(c => ({
		uniqueId: makeId('card'),
		sectionId: (c && c.sectionId && idMap[c.sectionId]) || (c && c.sectionId) || (sections[0] && sections[0].uniqueId) || '',
		type: c && c.type,
		name: c && c.name,
		layout: Object.assign({}, (c && c.layout) || {}),
		config: JSON.parse(JSON.stringify((c && c.config) || {}))
	}));
	return {
		sections,
		cards,
		icon: src.icon || DEFAULT_ICON,
		description: src.id === 'custom' ? '' : (src.description || ''),
		defaultTimeRange: src.defaultTimeRange || 'week'
	};
}

/**
 * Editor draft from a saved dashboard.
 * @param {object} dashboard
 * @returns {object}
 */
export function hydrateDashboardDraft(dashboard) {
	return draftFromDashboard(dashboard);
}

/**
 * Empty layout. Older edit shells still call this on a new blank dashboard.
 * @returns {{ sections: Array, cards: Array }}
 */
export function sampleBuildingOps() {
	return { sections: [], cards: [] };
}
